import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { listStatusPembayaran } from "@/constants"
import {
  getListJalurJenis,
  getListJenisUkt,
  getListUnitkerja,
} from "@/services/getData"
import { JalurJenis, JenisUkt, StatusPembayaran, Unitkerja } from "@/types"
import { ChevronRight } from "lucide-react"
import React, { useState } from "react"
import { LuListFilter } from "react-icons/lu"
import { useQuery } from "@tanstack/react-query"
import { useNavigate, useSearchParams } from "react-router-dom"

export default function FormFilter({ closeModal }: { closeModal: () => void }) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const idsRefJalurJenis = searchParams.get("ids_ref_jalur_jenis")
  const idsUk = searchParams.get("ids_uk")
  const idsJenisUkt = searchParams.get("ids_jenis_ukt")
  const statussPembayaran = searchParams.get("statuss_pembayaran")

  const [selectedFilter, setSelectedFilter] = useState("STATUS_PEMBAYARAN")
  const [filter, setFilter] = useState<{
    jalur_jenis: string[]
    program_studi: string[]
    jenis_ukt: string[]
    status_pembayaran: string[]
  }>({
    jalur_jenis: idsRefJalurJenis?.split("-") || [],
    program_studi: idsUk?.split("-") || [],
    jenis_ukt: idsJenisUkt?.split("-") || [],
    status_pembayaran: statussPembayaran?.split("-") || [],
  })

  const { data: listJenisUkt, isPending: isPendingListJenisUkt } = useQuery({
    queryKey: ["listJenisUkt"],
    queryFn: getListJenisUkt,
  })

  const { data: listJalurJenis, isPending: isPendingListJalurJenis } = useQuery(
    {
      queryKey: ["listJalurJenis"],
      queryFn: getListJalurJenis,
    }
  )

  const { data: listUnitkerja, isPending: isPendingListUnitkerja } = useQuery({
    queryKey: ["listUnitkerja"],
    queryFn: getListUnitkerja,
  })
  const listProdi = listUnitkerja?.filter((unitkerja: Unitkerja) => {
    return unitkerja.unitkerja_group_key == "5" && unitkerja.is_active
  })

  const handleApplyFilter = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (filter.status_pembayaran.length > 0) {
      searchParams.set("statuss_pembayaran", filter.status_pembayaran.join("-"))
    } else {
      searchParams.delete("statuss_pembayaran")
    }

    if (filter.jalur_jenis.length > 0) {
      searchParams.set("ids_ref_jalur_jenis", filter.jalur_jenis.join("-"))
    } else {
      searchParams.delete("ids_ref_jalur_jenis")
    }

    if (filter.program_studi.length > 0) {
      searchParams.set("ids_uk", filter.program_studi.join("-"))
    } else {
      searchParams.delete("ids_uk")
    }

    if (filter.jenis_ukt.length > 0) {
      searchParams.set("ids_jenis_ukt", filter.jenis_ukt.join("-"))
    } else {
      searchParams.delete("ids_jenis_ukt")
    }

    navigate(`/admin/data-ukt-pendaftar-lulus?${searchParams.toString()}`)

    closeModal()
  }

  return (
    <form
      onSubmit={handleApplyFilter}
      className="pb-20 relative flex flex-col gap-y-3"
    >
      <FormHeader title="Filter Data Pendaftaran" icon={<LuListFilter />} />

      <section className="p-3 relative border rounded-xl">
        <div
          className="flex justify-between items-center"
          onClick={() =>
            setSelectedFilter((prev) =>
              prev === "STATUS_PEMBAYARAN" ? "" : "STATUS_PEMBAYARAN"
            )
          }
        >
          <span className="font-bold">Status Pembayaran</span>
          <Button type="button" size={"icon"} variant={"outline"}>
            <ChevronRight
              className={`${
                selectedFilter === "STATUS_PEMBAYARAN" ? "rotate-90" : ""
              } duration-300`}
            />
          </Button>
        </div>
        {selectedFilter === "STATUS_PEMBAYARAN" && (
          <div className="mt-3 grid grid-cols-3 gap-5">
            {listStatusPembayaran?.map((statusPembayaran: StatusPembayaran) => {
              const isChecked = filter.status_pembayaran.includes(
                String(statusPembayaran.status)
              )

              return (
                <div key={statusPembayaran.status} className="flex gap-3">
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={(checked) => {
                      setFilter((prev) => {
                        const updatedStatusPembayaran = [
                          ...prev.status_pembayaran,
                        ]

                        if (checked) {
                          if (
                            !updatedStatusPembayaran.includes(
                              String(statusPembayaran.status)
                            )
                          ) {
                            updatedStatusPembayaran.push(
                              String(statusPembayaran.status)
                            )
                          }
                        } else {
                          const index = updatedStatusPembayaran.indexOf(
                            String(statusPembayaran.status)
                          )
                          if (index > -1) {
                            updatedStatusPembayaran.splice(index, 1)
                          }
                        }

                        return {
                          ...prev,
                          status_pembayaran: updatedStatusPembayaran,
                        }
                      })
                    }}
                  />
                  <Label>{statusPembayaran.text}</Label>
                </div>
              )
            })}
          </div>
        )}
      </section>

      <section className="p-3 relative border rounded-xl">
        <div
          className="flex justify-between items-center"
          onClick={() =>
            setSelectedFilter((prev) =>
              prev === "JENIS_UKT" ? "" : "JENIS_UKT"
            )
          }
        >
          <span className="font-bold">Jenis UKT</span>
          <Button type="button" size={"icon"} variant={"outline"}>
            <ChevronRight
              className={`${
                selectedFilter === "JENIS_UKT" ? "rotate-90" : ""
              } duration-300`}
            />
          </Button>
        </div>
        {selectedFilter === "JENIS_UKT" &&
          (isPendingListJenisUkt ? (
            "Loading..."
          ) : (
            <div className="mt-3 grid grid-cols-3 gap-5">
              {listJenisUkt?.map((jenisUkt: JenisUkt) => {
                const isChecked = filter.jenis_ukt.includes(
                  String(jenisUkt.id_ref_jenis_ukt)
                )

                return (
                  <div key={jenisUkt.id_ref_jenis_ukt} className="flex gap-3">
                    <Checkbox
                      checked={isChecked}
                      onCheckedChange={(checked) => {
                        setFilter((prev) => {
                          const updatedJenisUkt = [...prev.jenis_ukt]

                          if (checked) {
                            if (
                              !updatedJenisUkt.includes(
                                String(jenisUkt.id_ref_jenis_ukt)
                              )
                            ) {
                              updatedJenisUkt.push(
                                String(jenisUkt.id_ref_jenis_ukt)
                              )
                            }
                          } else {
                            const index = updatedJenisUkt.indexOf(
                              String(jenisUkt.id_ref_jenis_ukt)
                            )
                            if (index > -1) {
                              updatedJenisUkt.splice(index, 1)
                            }
                          }

                          return {
                            ...prev,
                            jenis_ukt: updatedJenisUkt,
                          }
                        })
                      }}
                    />
                    <Label>{jenisUkt.nama_jenis_ukt}</Label>
                  </div>
                )
              })}
            </div>
          ))}
      </section>

      <section className="p-3 relative border rounded-xl">
        <div
          className="flex justify-between items-center"
          onClick={() =>
            setSelectedFilter((prev) => (prev === "JALUR" ? "" : "JALUR"))
          }
        >
          <span className="font-bold">Jalur</span>
          <Button type="button" size={"icon"} variant={"outline"}>
            <ChevronRight
              className={`${
                selectedFilter === "JALUR" ? "rotate-90" : ""
              } duration-300`}
            />
          </Button>
        </div>
        {selectedFilter === "JALUR" &&
          (isPendingListJalurJenis ? (
            "Loading..."
          ) : (
            <div className="mt-3 grid grid-cols-3 gap-5">
              {listJalurJenis?.map((jalurJenis: JalurJenis) => {
                const isChecked = filter.jalur_jenis.includes(
                  String(jalurJenis.id_ref_jalur_jenis)
                )

                return (
                  <div
                    key={jalurJenis.id_ref_jalur_jenis}
                    className="flex gap-3"
                  >
                    <Checkbox
                      checked={isChecked}
                      onCheckedChange={(checked) => {
                        setFilter((prev) => {
                          const updatedJalurJenis = [...prev.jalur_jenis]

                          if (checked) {
                            if (
                              !updatedJalurJenis.includes(
                                String(jalurJenis.id_ref_jalur_jenis)
                              )
                            ) {
                              updatedJalurJenis.push(
                                String(jalurJenis.id_ref_jalur_jenis)
                              )
                            }
                          } else {
                            const index = updatedJalurJenis.indexOf(
                              String(jalurJenis.id_ref_jalur_jenis)
                            )
                            if (index > -1) {
                              updatedJalurJenis.splice(index, 1)
                            }
                          }

                          return {
                            ...prev,
                            jalur_jenis: updatedJalurJenis,
                          }
                        })
                      }}
                    />
                    <Label>{jalurJenis.nama_jalur_jenis}</Label>
                  </div>
                )
              })}
            </div>
          ))}
      </section>

      <section className="p-3 relative border rounded-xl max-h-[30vh] overflow-auto">
        <div
          className="flex justify-between items-center"
          onClick={() =>
            setSelectedFilter((prev) => (prev === "PRODI" ? "" : "PRODI"))
          }
        >
          <span className="font-bold">Program Studi</span>
          <Button type="button" size={"icon"} variant={"outline"}>
            <ChevronRight
              className={`${
                selectedFilter === "PRODI" ? "rotate-90" : ""
              } duration-300`}
            />
          </Button>
        </div>
        {selectedFilter === "PRODI" &&
          (isPendingListUnitkerja ? (
            "Loading..."
          ) : (
            <div className="mt-3 grid grid-cols-2 gap-5">
              {listProdi?.map((prodi: Unitkerja) => {
                const isChecked = (filter.program_studi as string[]).includes(
                  String(prodi.uk_id)
                )

                return (
                  <div key={prodi.uk_id} className="flex gap-3">
                    <Checkbox
                      checked={isChecked}
                      onCheckedChange={(checked) => {
                        setFilter((prev) => {
                          const programStudi = [...prev.program_studi]

                          if (checked) {
                            if (!programStudi.includes(String(prodi.uk_id))) {
                              programStudi.push(String(prodi.uk_id))
                            }
                          } else {
                            const index = programStudi.indexOf(
                              String(prodi.uk_id)
                            )
                            if (index > -1) {
                              programStudi.splice(index, 1)
                            }
                          }

                          return {
                            ...prev,
                            program_studi: programStudi,
                          }
                        })
                      }}
                    />
                    <Label>{prodi.nama}</Label>
                  </div>
                )
              })}
            </div>
          ))}
      </section>

      <div className="absolute bottom-3 left-3 right-3">
        <div className="flex justify-between">
          <div className="flex gap-3">
            <Button
              onClick={() => closeModal()}
              type="button"
              variant={"outline"}
            >
              Batal
            </Button>
            <Button>Terapkan Filter</Button>
          </div>
          <Button
            type="button"
            variant={"outline"}
            onClick={() => {
              setFilter((prev) => ({
                ...prev,
                jalur_jenis: [],
                program_studi: [],
                jenis_ukt: [],
                status_pembayaran: [],
              }))
              navigate("/admin/data-ukt-pendaftar-lulus")
            }}
          >
            Reset
          </Button>
        </div>
      </div>
    </form>
  )
}
